import { PluginConfig } from "@internal/backstage-plugin-digital-core-api-common";
import {
    type ApiResponse,
    BackendClient,
    type BackendClientParameters,
} from "@internal/backstage-plugin-utility-common";

import type {
    AddFavoriteInstitutionClientParams,
    AddFavoriteInstitutionResult,
    AssignFirefighterGroupsResult,
    ListFavoriteInstitutionsResult,
    ListFirefighterStatusResult,
    RemoveFavoriteInstitutionClientParams,
    RemoveFavoriteInstitutionResult,
    RemoveFirefighterGroupsResult,
    RevokeAllFirefighterGroupsResult,
} from "../types";

/**
 * Parameters for creating a FirefighterClient
 *
 * @public
 */
export type FirefighterClientOptions = Omit<
    BackendClientParameters,
    "targetPluginId"
>;

/**
 * Client to fetch data from the firefighter backend
 * The backend automatically handles JHID token exchange using the user's session.
 *
 * @public
 */
export class FirefighterClient extends BackendClient {
    constructor(options: FirefighterClientOptions) {
        super({
            discoveryApi: options.discoveryApi,
            identityApi: options.identityApi,
            targetPluginId: "firefighter",
        });
    }

    /**
     * List firefighter status for the authenticated user across all institutions
     *
     * @returns API response with firefighter status information for all institutions
     *
     * @example
     * ```typescript
     * const client = new FirefighterClient({ discoveryApi, identityApi });
     * const result = await client.listFirefighterStatus();
     * if (result.ok) {
     *   console.log('Firefighter status:', result.data);
     * }
     * ```
     */
    public async listFirefighterStatus(): Promise<
        ApiResponse<ListFirefighterStatusResult>
    > {
        return this.api("listFirefighterStatus", {
            method: "GET",
        });
    }

    /**
     * Request firefighter permissions for the authenticated user
     *
     * @param params - Parameters containing institutionUniversalId
     * @returns API response with array of assigned firefighter groups
     *
     * @example
     * ```typescript
     * const client = new FirefighterClient({ discoveryApi, identityApi });
     * const result = await client.requestFirefighterPerms({
     *   institutionUniversalId: 'inst-123',
     * });
     * if (result.ok) {
     *   console.log('Assigned groups:', result.data);
     * }
     * ```
     */
    public async requestFirefighterPerms(params: {
        institutionUniversalId: string;
    }): Promise<ApiResponse<AssignFirefighterGroupsResult>> {
        return this.api("assignFirefighterGroups", {
            method: "POST",
            body: JSON.stringify(params),
            headers: {
                "Content-Type": "application/json",
            },
        });
    }

    /**
     * Remove firefighter permissions from the authenticated user for a specific institution
     *
     * @param params - Parameters containing institutionUniversalId
     * @returns API response (void on success)
     *
     * @example
     * ```typescript
     * const client = new FirefighterClient({ discoveryApi, identityApi });
     * const result = await client.removeFirefighterPerms({
     *   institutionUniversalId: 'inst-123',
     * });
     * if (result.ok) {
     *   console.log('Firefighter permissions removed from institution');
     * }
     * ```
     */
    public async removeFirefighterPerms(params: {
        institutionUniversalId: string;
    }): Promise<ApiResponse<RemoveFirefighterGroupsResult>> {
        return this.api("removeFirefighterGroups", {
            method: "POST",
            body: JSON.stringify(params),
            headers: {
                "Content-Type": "application/json",
            },
        });
    }

    /**
     * Revoke all firefighter permissions from the authenticated user across all institutions
     *
     * @returns API response (void on success)
     *
     * @example
     * ```typescript
     * const client = new FirefighterClient({ discoveryApi, identityApi });
     * const result = await client.revokeAllFirefighterPerms();
     * if (result.ok) {
     *   console.log('All firefighter permissions revoked');
     * }
     * ```
     */
    public async revokeAllFirefighterPerms(): Promise<
        ApiResponse<RevokeAllFirefighterGroupsResult>
    > {
        return this.api("revokeAllFirefighterGroups", {
            method: "POST",
        });
    }

    /**
     * Get Digital Core API configuration
     * Retrieves the enterprise API base URL from the backend
     *
     * @returns API response with Digital Core API configuration
     *
     * @example
     * ```typescript
     * const client = new FirefighterClient({ discoveryApi, identityApi });
     * const result = await client.getDigitalCoreApiConfig();
     * if (result.ok && result.data.enterpriseApi?.baseUrl) {
     *   console.log('Enterprise API Base URL:', result.data.enterpriseApi.baseUrl);
     * }
     * ```
     */
    public async getDigitalCoreApiConfig(): Promise<ApiResponse<PluginConfig>> {
        return this.api("digitalCoreApiConfig", {
            method: "GET",
        });
    }

    /**
     * List favorite institutions for the authenticated user
     *
     * @returns API response with array of institution IDs that are favorited
     *
     * @example
     * ```typescript
     * const client = new FirefighterClient({ discoveryApi, identityApi });
     * const result = await client.listFavoriteInstitutions();
     * if (result.ok) {
     *   console.log('Favorite institution IDs:', result.data);
     * }
     * ```
     */
    public async listFavoriteInstitutions(): Promise<
        ApiResponse<ListFavoriteInstitutionsResult>
    > {
        return this.api("listFavoriteInstitutions", {
            method: "GET",
        });
    }

    /**
     * Add an institution to the authenticated user's favorites
     *
     * @param params - Parameters containing institutionId
     * @returns API response (void on success)
     *
     * @example
     * ```typescript
     * const client = new FirefighterClient({ discoveryApi, identityApi });
     * const result = await client.addFavoriteInstitution({ institutionId: 'inst-123' });
     * if (result.ok) {
     *   console.log('Institution added to favorites');
     * }
     * ```
     */
    public async addFavoriteInstitution(
        params: AddFavoriteInstitutionClientParams
    ): Promise<ApiResponse<AddFavoriteInstitutionResult>> {
        return this.api("addFavoriteInstitution", {
            method: "POST",
            body: JSON.stringify(params),
            headers: {
                "Content-Type": "application/json",
            },
        });
    }

    /**
     * Remove an institution from the authenticated user's favorites
     *
     * @param params - Parameters containing institutionId
     * @returns API response (void on success)
     *
     * @example
     * ```typescript
     * const client = new FirefighterClient({ discoveryApi, identityApi });
     * const result = await client.removeFavoriteInstitution({ institutionId: 'inst-123' });
     * if (result.ok) {
     *   console.log('Institution removed from favorites');
     * }
     * ```
     */
    public async removeFavoriteInstitution(
        params: RemoveFavoriteInstitutionClientParams
    ): Promise<ApiResponse<RemoveFavoriteInstitutionResult>> {
        return this.api<RemoveFavoriteInstitutionResult>(
            "removeFavoriteInstitution",
            {
                method: "POST",
                body: JSON.stringify(params),
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );
    }
}
